import type { DestinationMetadata } from '@/scopes/search/quick-search/domain/types'
import type { FilterOption } from '@/scopes/search/types'
import { useIntl } from '@/intl'
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBarLegacy/DialogPrimitive'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBarLegacy/store'
import {
  Button,
  ButtonLoaderIcon,
  Icon,
  SheetClose,
  SheetContentWrapper,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import * as React from 'react'
import { styled } from 'styled-components'
import { Item } from '../QuickSearchBarLegacy'
import * as DestinationFilterPrimitive from './DestinationFilterPrimitive'

const SheetFooterStyled = styled(SheetFooter)`
  display: flex;
  flex-direction: row;
  bottom: 0;
`

const DialogFooter = styled(DialogPrimitive.Footer)`
  padding: unset;
`

export function DestinationFilterComponent({
  disabled,
  onConfirm,
  options,
  selectedOption,
}: {
  disabled?: boolean
  onConfirm: (selection: FilterOption<string, DestinationMetadata> | undefined) => void
  options: FilterOption<string, DestinationMetadata>[]
  selectedOption?: FilterOption<string, DestinationMetadata>
}) {
  const intl = useIntl()
  const { setActiveItem } = useQuickSearchStore()

  const handleClick = (selection: FilterOption<string, DestinationMetadata>) => {
    onConfirm(selection)
    setActiveItem(null)
  }

  const handleClose = () => {
    setActiveItem(null)
  }

  const onClear = () => {
    handleClose()
    onConfirm(undefined)
  }

  const defaultOptions = options.filter(o => o.metadata.type === 'Country' || o.metadata.type === 'Region')

  return (
    <Item.Root
      id="travel-destination-filter"
      icon="IconClock"
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_destination_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_destination_title',
      })}
      onClose={handleClose}
    >
      <Item.AsPopover
        selectionFormattedText={selectedOption?.name}
        disabled={disabled}
        alignment="start"
        data-qa="destination-filter"
      >
        <SpaceBetween direction="y" spacing={tk('spacing_md')}>
          <DestinationFilterPrimitive.Root
            options={options}
            defaultOptions={defaultOptions}
            onItemClick={handleClick}
          />
          <DestinationFilterPrimitive.Footer>
            <Button $fullSize onClick={onClear} disabled={disabled} $hierarchy="tertiary" $withoutColor>
              {intl.formatMessage({ id: 'quicksearch_clear_button_text' })}
            </Button>
            <Button $fullSize onClick={handleClose} disabled={disabled}>
              {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
            </Button>
          </DestinationFilterPrimitive.Footer>
        </SpaceBetween>
      </Item.AsPopover>
      <Item.AsSheet selectionFormattedText={selectedOption?.name} disabled={disabled} data-qa="destination-filter">
        <>
          <SheetHeader>
            <SheetTitle>{intl.formatMessage({ id: 'quicksearch_filter_destination_title' })}</SheetTitle>
            <SheetClose>{intl.formatMessage({ id: 'quicksearch_cancel_button_text' })}</SheetClose>
          </SheetHeader>
          <SheetContentWrapper>
            <DestinationFilterPrimitive.Root
              options={options}
              defaultOptions={defaultOptions}
              onItemClick={handleClick}
            />
          </SheetContentWrapper>
          <SheetFooterStyled $isFixed={false}>
            <Button $fullSize onClick={onClear} disabled={disabled} $hierarchy="tertiary" $withoutColor>
              {intl.formatMessage({ id: 'quicksearch_clear_button_text' })}
            </Button>
            <Button $fullSize onClick={handleClose} disabled={disabled}>
              {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
            </Button>
          </SheetFooterStyled>
        </>
      </Item.AsSheet>
      <Item.AsDialog selectionFormattedText={selectedOption?.name} disabled={disabled} data-qa="destination-filter">
        <DialogPrimitive.Root>
          <DialogPrimitive.Header>
            <DialogPrimitive.HeaderTitle>
              <Typography $variant="body-lg-bold">
                {intl.formatMessage({ id: 'quicksearch_filter_destination_title' })}
              </Typography>
              <Button onClick={handleClose} $hierarchy="tertiary" $withoutColor>
                <Icon glyph="IconCrossMedium" size="md" />
              </Button>
            </DialogPrimitive.HeaderTitle>
          </DialogPrimitive.Header>
          <DialogPrimitive.Body>
            <DestinationFilterPrimitive.Root
              options={options}
              defaultOptions={defaultOptions}
              onItemClick={handleClick}
            />
          </DialogPrimitive.Body>
          <DialogFooter>
            <DestinationFilterPrimitive.Footer>
              <Button $fullSize onClick={onClear} disabled={disabled} $hierarchy="tertiary" $withoutColor>
                {intl.formatMessage({ id: 'quicksearch_clear_button_text' })}
              </Button>
              <Button $fullSize onClick={handleClose} disabled={disabled}>
                {disabled && <ButtonLoaderIcon />}
                {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
              </Button>
            </DestinationFilterPrimitive.Footer>
          </DialogFooter>
        </DialogPrimitive.Root>
      </Item.AsDialog>
      <Item.AsIcon selectionFormattedText={selectedOption?.name} compactSelectionFormattedText={selectedOption?.name} />
    </Item.Root>
  )
}
